import { Controller, FieldValues } from 'react-hook-form';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import RefreshOutlinedIcon from '@mui/icons-material/RefreshOutlined';
import SearchIcon from '@mui/icons-material/Search';
import {
  Button,
  Card,
  CircularProgress,
  Grid,
  Stack,
  Theme,
  Typography,
  useMediaQuery,
} from '@mui/material';

import { ListingFilterCardProps } from './prop';

export const ListingFilterCard = <T extends FieldValues>(
  props: ListingFilterCardProps<T>
) => {
  const {
    gridContainerProps,
    gridItemProps,
    formItems,
    control,
    onExport,
    onReset,
    onSubmit,
    exportLoading = false,
    title = 'Search',
  } = props;

  const isSM = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  // ======================= RENDER FUNCTIONS
  const renderSMButtonGroup = () => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mt={4}
      spacing={4}
    >
      <Button
        type="submit"
        variant="contained"
        startIcon={<SearchIcon />}
        fullWidth
      >
        Search
      </Button>
      <Button
        variant="outlined"
        onClick={onReset}
        sx={{ minWidth: 0, paddingX: 4 }}
      >
        <RefreshOutlinedIcon sx={{ fontSize: 20 }} />
      </Button>
      {onExport && (
        <Button
          variant="outlined"
          onClick={onExport}
          sx={{ minWidth: 0, paddingX: 4 }}
          disabled={exportLoading}
        >
          {exportLoading ? (
            <CircularProgress size={20} sx={{ color: 'rgba(0, 0, 0, 0.26)' }} />
          ) : (
            <FileUploadOutlinedIcon sx={{ fontSize: 20 }} />
          )}
        </Button>
      )}
    </Stack>
  );

  const renderButtonGroup = () => (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      mt={4}
    >
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        spacing={5}
      >
        <Button type="submit" variant="contained" startIcon={<SearchIcon />}>
          Search
        </Button>
        <Button
          variant="outlined"
          onClick={onReset}
          sx={{ minWidth: 0, paddingX: 4 }}
        >
          Reset
        </Button>
      </Stack>
      {onExport && (
        <Button
          variant="outlined"
          startIcon={
            exportLoading ? (
              <CircularProgress
                size={20}
                sx={{ color: 'rgba(0, 0, 0, 0.26)' }}
              />
            ) : (
              <FileUploadOutlinedIcon />
            )
          }
          disabled={exportLoading}
          onClick={onExport}
          sx={{ minWidth: 0, paddingX: 4 }}
        >
          Export
        </Button>
      )}
    </Stack>
  );

  // ======================= VIEWS
  return (
    <Card sx={{ p: 5 }}>
      <Stack spacing={4}>
        <Typography variant="h6">{title}</Typography>

        <form onSubmit={onSubmit}>
          <Grid
            container
            columnSpacing={6}
            rowSpacing={4}
            direction="row"
            {...gridContainerProps}
          >
            {formItems.map((item) => {
              return (
                <Grid
                  key={item.name}
                  item
                  lg={4}
                  md={6}
                  xs={12}
                  {...gridItemProps}
                >
                  <Controller
                    control={control}
                    name={item.name}
                    render={item.render}
                  />
                </Grid>
              );
            })}
          </Grid>
          {isSM ? renderSMButtonGroup() : renderButtonGroup()}
        </form>
      </Stack>
    </Card>
  );
};

// ======================= EXPORT
export default ListingFilterCard;
